import React from 'react';
import { 
    Card, CardContent, CardActions, Button, TextField, MenuItem, 
    Box, Typography, FormControlLabel, Checkbox, FormGroup, CircularProgress 
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import FileDropZone from './FileDropZone';
import AudioPlayer from './AudioPlayer';
import PromptsSelect from './PromptsSelect';
import ParticipantsInput from './ParticipantsInput';

const UploadForm = ({
    handleSubmit,
    handleRecordingNameChange,
    recordingName,
    handleFileSelect,
    file,
    language,
    handleLanguageChange,
    transcriptionService,
    setTranscriptionService,
    shouldSummarize,
    setShouldSummarize,
    setSelectedPrompt,
    setShouldGeneratePresentation,
    outputLanguage,
    handleOutputLanguageChange,
    SUMMARY_MODELS,
    selectedModels,
    handleModelChange,
    selectedPrompt,
    shouldGeneratePresentation,
    presentationNumCards,
    setPresentationNumCards,
    presentationTextAmount,
    setPresentationTextAmount,
    audioDuration,
    audioUrl,
    cropStart,
    cropEnd,
    setCropStart,
    setCropEnd,
    isUploading,
    participants,
    setParticipants,
    DETAIL_LEVELS,
    detailLevel,
    setDetailLevel
}) => {
    return (
        <Card>
            <CardContent>
                <form onSubmit={handleSubmit}>
                    <TextField
                        label="Recording Name"
                        value={recordingName}
                        onChange={handleRecordingNameChange}
                        required
                        fullWidth
                        margin="normal"
                    />
                    
                    <Box sx={{ mt: 2, mb: 2 }}>
                        <FileDropZone
                            onFileSelect={handleFileSelect}
                            selectedFile={file}
                            accept="audio/*, .m4a, .mp3, .wav, .aac, .ogg"
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                        <TextField
                            select
                            label="Input Language"
                            value={language}
                            onChange={handleLanguageChange}
                            required
                            fullWidth
                        >
                            <MenuItem value="hebrew">Hebrew</MenuItem>
                            <MenuItem value="english">English</MenuItem>
                        </TextField>

                        {language !== 'english' && (
                            <TextField
                                select
                                label="Transcription Service"
                                value={transcriptionService}
                                onChange={(e) => setTranscriptionService(e.target.value)}
                                required
                                fullWidth
                            >
                                <MenuItem value="soniox">Soniox</MenuItem>
                                <MenuItem value="gemini">Gemini 2.5 Pro</MenuItem>
                            </TextField>
                        )}

                        {language === 'english' && (
                            <TextField
                                select
                                label="Transcription Service"
                                value={transcriptionService}
                                onChange={(e) => setTranscriptionService(e.target.value)}
                                required
                                fullWidth
                            >
                                <MenuItem value="soniox">Soniox</MenuItem>
                                <MenuItem value="deepgram">Deepgram</MenuItem>
                            </TextField>
                        )}
                    </Box>
                    
                    <Box sx={{ 
                        mt: 3, 
                        mb: 2, 
                        p: 2, 
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1
                    }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Summary Generation
                        </Typography>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={shouldSummarize}
                                    onChange={(e) => {
                                        setShouldSummarize(e.target.checked);
                                        if (!e.target.checked) {
                                            setSelectedPrompt(null);
                                            setShouldGeneratePresentation(false);
                                        }
                                    }}
                                    name="shouldSummarize"
                                />
                            }
                            label="Generate Summary"
                        />
                        {shouldSummarize && (
                            <>
                                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                    <TextField
                                        select
                                        label="Summary Language"
                                        value={outputLanguage}
                                        onChange={handleOutputLanguageChange}
                                        required
                                        fullWidth
                                        size="small"
                                    >
                                        <MenuItem value="hebrew">Hebrew</MenuItem>
                                        <MenuItem value="english">English</MenuItem>
                                    </TextField>

                                    <TextField
                                        select
                                        label="Detail Level"
                                        value={detailLevel}
                                        onChange={(e) => setDetailLevel(e.target.value)}
                                        fullWidth
                                        size="small"
                                        helperText={detailLevel === 'detailed' ? 'Enforces longer, more thorough summaries for transcripts over 2,000 words' : ''}
                                    >
                                        {DETAIL_LEVELS.map((level) => (
                                            <MenuItem key={level.id} value={level.id}>{level.name}</MenuItem>
                                        ))}
                                    </TextField>
                                </Box>
                                
                                <Box sx={{ mt: 2, mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Select Models
                                    </Typography>
                                    <FormGroup row>
                                        {SUMMARY_MODELS.map((model) => (
                                            <FormControlLabel
                                                key={model.id}
                                                control={
                                                    <Checkbox 
                                                        checked={selectedModels.includes(model.id)}
                                                        onChange={handleModelChange}
                                                        value={model.id}
                                                        disabled={selectedModels.length === 1 && selectedModels.includes(model.id)}
                                                    />
                                                }
                                                label={model.name}
                                            />
                                        ))}
                                    </FormGroup>
                                </Box>
                                
                                <Box sx={{ mt: 2 }}>
                                    <PromptsSelect
                                        value={selectedPrompt}
                                        onChange={(prompt) => setSelectedPrompt(prompt)}
                                    />
                                </Box>

                                <ParticipantsInput
                                    value={participants}
                                    onChange={setParticipants}
                                />

                                {/* Gamma Presentation Generation - Nested under Summary */}
                                <Box sx={{ mt: 3, p: 2, backgroundColor: 'action.hover', borderRadius: 1, border: '1px dashed', borderColor: 'primary.light' }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={shouldGeneratePresentation}
                                                onChange={(e) => setShouldGeneratePresentation(e.target.checked)}
                                                name="shouldGeneratePresentation"
                                            />
                                        }
                                        label="Generate Presentation"
                                    />
                                    {shouldGeneratePresentation && (
                                        <>
                                            <Box sx={{ mt: 2, mb: 2 }}>
                                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                                    Number of Cards: {presentationNumCards}
                                                </Typography>
                                                <input
                                                    type="range"
                                                    min="10"
                                                    max="20"
                                                    value={presentationNumCards}
                                                    onChange={(e) => setPresentationNumCards(parseInt(e.target.value))}
                                                    style={{ width: '100%' }}
                                                />
                                            </Box>

                                            <TextField
                                                select
                                                label="Detail Level"
                                                value={presentationTextAmount}
                                                onChange={(e) => setPresentationTextAmount(e.target.value)}
                                                fullWidth
                                                margin="normal"
                                                size="small"
                                            >
                                                <MenuItem value="brief">Brief</MenuItem>
                                                <MenuItem value="medium">Medium</MenuItem>
                                                <MenuItem value="detailed">Detailed</MenuItem>
                                                <MenuItem value="extensive">Extensive</MenuItem>
                                            </TextField>

                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                Language will be derived from summary language setting
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                            </>
                        )}
                    </Box>

                    {audioDuration > 0 && (
                        <Box sx={{ mt: 2, mb: 2 }}>
                            {audioUrl && (
                                <AudioPlayer 
                                    audioUrl={audioUrl}
                                    file={file}
                                    duration={audioDuration}
                                    cropStart={cropStart}
                                    cropEnd={cropEnd}
                                    onTimeUpdate={(time) => {
                                        // We can use this for future features if needed
                                    }}
                                    onCropChange={(start, end) => {
                                        setCropStart(start);
                                        setCropEnd(end);
                                    }}
                                />
                            )}
                        </Box>
                    )}
                </form>
            </CardContent>
            <CardActions>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={isUploading || !file || !language || !recordingName || (shouldSummarize && (!selectedPrompt || !selectedPrompt.content?.trim()))}
                    startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <CloudUpload/>}
                    fullWidth
                >
                    {isUploading ? 'Uploading...' : 'Start Process'}
                </Button>
            </CardActions>
        </Card>
    );
};

export default UploadForm;
